import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ResetPassword from '../components/ResetPassword';
import NewPassword from '../components/NewPassword';
import { getAuthCookieAttributes, getCookie } from 'shared/auth/utils/cookieUtils';
import { verifyToken } from 'shared/api/client/orvalHelpers';
import { useTokenProcessing } from 'shared/auth/hooks/useTokenProcessing';
import { getApiOrigin } from 'shared/api/client/apiOrigin';
import { normalizeUserRole } from 'shared/auth/constants/roles';

import '../../../styles/legacy/Login.css';

const LoginPage = () => {
  useTokenProcessing();
  const navigate = useNavigate();
  const location = useLocation();
  
  const userType = 'Creator';
  const pageTitle = 'Poetics Stream';

  const GOOGLE_OAUTH_URL = `${getApiOrigin()}/api/User/google-login/${userType}`;
  const FACEBOOK_OAUTH_URL = `${getApiOrigin()}/api/User/facebook-login/${userType}`;

  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showSetNewPassword, setShowSetNewPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [showLoginForm, setShowLoginForm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [successMessage, onSuccess] = useState('');
  const [wrongCredentials, setWrongCredentials] = useState(false);
  const [googleAccountExists, setGoogleAccountExists] = useState(false);
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => onSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (showResetPassword || showSetNewPassword) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showResetPassword, showSetNewPassword]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromURL = params.get('resetToken');

    if (tokenFromURL) {
      setShowSetNewPassword(true);
    }
  }, [location]);

  useEffect(() => {
    const token = getCookie('userToken');
    if (!token) {
      setIsVerified(false);
      return;
    }
    verifyToken(token).then((result: boolean) => setIsVerified(result));
  }, []);

  if (isVerified === null) {
    return (<>
          <title>{pageTitle}</title>
        Loading...
      </>);
  }

  const handleLogin = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setLoading(true);
    setShowLoginForm(false);
    setWrongCredentials(false);
    setGoogleAccountExists(false);

    const emailInput = document.querySelector<HTMLInputElement>(
      'input[placeholder="Email"]'
    );
    const passwordInput = document.querySelector<HTMLInputElement>(
      'input[placeholder="Password"]'
    );

    if (!emailInput || !passwordInput) {
      return;
    }

    const email = emailInput.value;
    const password = passwordInput.value;

    const payload = { email, password };

    try {
      const { userApi } = await import("shared/api/client/orval");
      const res = await userApi.postApiUserLogin(payload as any);
      const data = res.data as any;
      {
        const userToken = data.token;
        const userId = data.id;
        const userEmail = data.email;
        const userFirstName = data.first_name || data.firstName || '';
        const userLastName = data.last_name || data.lastName || '';
        const userIsVerified = data.user_is_verified;
        const userRole = data.user_role;
        const authCookieAttributes = getAuthCookieAttributes();
        document.cookie = `userToken=${userToken}; ${authCookieAttributes}`;
        document.cookie = `userId=${userId}; ${authCookieAttributes}`;
        document.cookie = `userEmail=${userEmail}; ${authCookieAttributes}`;
        document.cookie = `userFirstName=${userFirstName}; ${authCookieAttributes}`;
        document.cookie = `userLastName=${userLastName}; ${authCookieAttributes}`;
        document.cookie = `userIsVerified=${userIsVerified}; ${authCookieAttributes}`;
        document.cookie = `userRole=${userRole}; ${authCookieAttributes}`;
        setLoading(false);

        const normalized = normalizeUserRole(userRole);
        const landing =
          normalized === "USER"
            ? "/home"
            : normalized === "CURATOR"
              ? "/home/movie-review"
              : "/home/library";
        navigate(landing);
      }
    } catch (err: any) {
      const errorText = err?.response?.data ?? err?.message ?? "";
      const status = err?.response?.status;
      if (status === 401) {
        if (
          String(errorText).includes('Google account connected. Please login with Google.')
        ) {
          setGoogleAccountExists(true);
          setTimeout(() => setGoogleAccountExists(false), 5000);
        } else {
          setWrongCredentials(true);
          setTimeout(() => setWrongCredentials(false), 5000);
        }
        setLoading(false);
        setShowLoginForm(true);
      }
      setLoading(false);
      setShowLoginForm(true);
    } 
  }; 

  return (
    <div className='login-container'>
        <title>{pageTitle}</title>
      <div className='login-box'>
        <div className='li-logo-container'>
          <img src="/images/poetics-stream-lockup.svg" alt="Poetics Stream" style={{ height: 30, width: "auto" }} />
          <h1 className="app-logo-subtitle">BEYOND WATCHING</h1> 
        </div>

        {successMessage && (
          <div className='updated-message-frame'>
            <div className='login-error-message'>{successMessage}</div>
          </div>
        )}

        {wrongCredentials && (
          <div className='wrong-credentials-frame'>
            <svg
              width='16'
              height='20'
              viewBox='0 0 16 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g clipPath='url(#clip0_4660_13412)'>
                <path
                  d='M8 5C8.14972 5 8.29723 5.02931 8.43262 5.08496L8.56348 5.14941C8.69015 5.22238 8.79816 5.31876 8.88184 5.43066L8.95703 5.54688L15.5469 17.5293C15.6099 17.6439 15.6491 17.7678 15.6621 17.8945L15.666 18.0215C15.6619 18.1495 15.6318 18.2759 15.5771 18.3936L15.5146 18.5078C15.4441 18.6187 15.3515 18.7164 15.2412 18.7949L15.125 18.8672C14.9627 18.9543 14.7781 19 14.5898 19H1.41016L1.27051 18.9922C1.1778 18.9808 1.08719 18.958 1.00098 18.9248L0.875 18.8672C0.753415 18.8019 0.647449 18.7157 0.5625 18.6143L0.485352 18.5078C0.414747 18.3967 0.367911 18.2744 0.34668 18.1484L0.333984 18.0215C0.329882 17.8938 0.351822 17.7666 0.398438 17.6465L0.453125 17.5293L7.04395 5.54688L7.04297 5.5459C7.10988 5.42455 7.20316 5.31676 7.31641 5.22949L7.43652 5.14941C7.6053 5.0522 7.80044 5.00006 8 5ZM7.95801 14.9766C7.77791 14.9797 7.59932 15.0164 7.43359 15.0859C7.30901 15.1383 7.19241 15.2085 7.08887 15.2939L6.99023 15.3838C6.89551 15.4791 6.81549 15.5877 6.75391 15.7061L6.69922 15.8281C6.64997 15.9525 6.62011 16.0831 6.61133 16.2158L6.60938 16.3496C6.62179 16.6607 6.74441 16.9547 6.95117 17.1855L7.04492 17.2803C7.27497 17.4918 7.57001 17.6209 7.88086 17.6504L8.01465 17.6562H8.04004C8.17567 17.6542 8.31046 17.6328 8.43945 17.5928L8.56641 17.5469C8.69124 17.4945 8.8074 17.4244 8.91113 17.3389L9.01074 17.249C9.10565 17.1535 9.18548 17.0444 9.24707 16.9258L9.30176 16.8037C9.35099 16.6788 9.38022 16.5472 9.38867 16.4141L9.39062 16.2803C9.37774 15.9693 9.25485 15.6757 9.04785 15.4453L8.9541 15.3506H8.95312C8.72277 15.1395 8.42797 15.0113 8.11719 14.9824L7.9834 14.9766H7.95801ZM8 8.59277C7.76336 8.59277 7.53282 8.67078 7.34766 8.81543L7.27148 8.88086C7.0752 9.06775 6.96204 9.32499 6.96191 9.59668V13.625C6.962 13.8628 7.0488 14.0894 7.20215 14.2676L7.27148 14.3408C7.46732 14.5273 7.72975 14.6289 8 14.6289C8.23666 14.6289 8.46717 14.5509 8.65234 14.4062L8.72852 14.3408C8.92482 14.1539 9.03799 13.8967 9.03809 13.625V9.59668C9.03796 9.32499 8.9248 9.06775 8.72852 8.88086C8.53268 8.69441 8.27024 8.59277 8 8.59277Z'
                  fill='#FAFAFA'
                  stroke='#FAFAFA'
                  strokeWidth='0.666667'
                />
              </g>
              <defs>
                <clipPath id='clip0_4660_13412'>
                  <rect
                    width='16'
                    height='16'
                    fill='white'
                    transform='translate(0 4)'
                  />
                </clipPath>
              </defs>
            </svg>
            <div className='alert-text-wrapper'>
              <h4 className='wrong-title'>Wrong credentials.</h4>
              <p className='wrong-message'>
                The email and/or password you provided are wrong. Try again.
              </p>
            </div>
          </div>
        )}

        {googleAccountExists && (
          <div className='continue-with-Google-frame'>
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g clipPath='url(#clip0_4636_161)'>
                <path
                  d='M8 0C6.41775 0 4.87103 0.469192 3.55544 1.34824C2.23985 2.22729 1.21447 3.47672 0.608967 4.93853C0.00346632 6.40034 -0.15496 8.00887 0.153721 9.56072C0.462403 11.1126 1.22433 12.538 2.34315 13.6569C3.46197 14.7757 4.88743 15.5376 6.43928 15.8463C7.99113 16.155 9.59966 15.9965 11.0615 15.391C12.5233 14.7855 13.7727 13.7602 14.6518 12.4446C15.5308 11.129 16 9.58225 16 8C15.9977 5.87897 15.1541 3.84547 13.6543 2.34568C12.1545 0.845885 10.121 0.00229405 8 0ZM8.16667 3.33333C8.36445 3.33333 8.55779 3.39198 8.72224 3.50186C8.88669 3.61174 9.01486 3.76792 9.09055 3.95065C9.16624 4.13338 9.18604 4.33444 9.14745 4.52842C9.10887 4.7224 9.01363 4.90059 8.87378 5.04044C8.73392 5.18029 8.55574 5.27553 8.36176 5.31412C8.16778 5.3527 7.96671 5.3329 7.78399 5.25721C7.60126 5.18152 7.44508 5.05335 7.3352 4.8889C7.22532 4.72445 7.16667 4.53111 7.16667 4.33333C7.16667 4.06812 7.27203 3.81376 7.45956 3.62623C7.6471 3.43869 7.90145 3.33333 8.16667 3.33333ZM9.66667 12.3333H7C6.82319 12.3333 6.65362 12.2631 6.5286 12.1381C6.40357 12.013 6.33334 11.8435 6.33334 11.6667C6.33334 11.4899 6.40357 11.3203 6.5286 11.1953C6.65362 11.0702 6.82319 11 7 11H7.5C7.54421 11 7.5866 10.9824 7.61785 10.9512C7.64911 10.9199 7.66667 10.8775 7.66667 10.8333V7.83333C7.66667 7.78913 7.64911 7.74674 7.61785 7.71548C7.5866 7.68423 7.54421 7.66667 7.5 7.66667H7C6.82319 7.66667 6.65362 7.59643 6.5286 7.4714C6.40357 7.34638 6.33334 7.17681 6.33334 7C6.33334 6.82319 6.40357 6.65362 6.5286 6.52859C6.65362 6.40357 6.82319 6.33333 7 6.33333H7.66667C8.02029 6.33333 8.35943 6.47381 8.60948 6.72386C8.85953 6.97391 9 7.31304 9 7.66667V10.8333C9 10.8775 9.01756 10.9199 9.04882 10.9512C9.08007 10.9824 9.12247 11 9.16667 11H9.66667C9.84348 11 10.013 11.0702 10.1381 11.1953C10.2631 11.3203 10.3333 11.4899 10.3333 11.6667C10.3333 11.8435 10.2631 12.013 10.1381 12.1381C10.013 12.2631 9.84348 12.3333 9.66667 12.3333Z'
                  fill='#FAFAFA'
                />
              </g>
              <defs>
                <clipPath id='clip0_4636_161'>
                  <rect width='16' height='16' fill='white' />
                </clipPath>
              </defs>
            </svg>
            <div className='alert-text-wrapper'>
              <h4 className='continue-with-Google-title'>
                Continue with Google.
              </h4>
              <p className='continue-with-Google-message'>
                This email is already associated with a Google account.
              </p>
            </div>
          </div>
        )}

        <div className='welcome-container'>
          <h1 className='welcome'>Welcome back</h1>
          <p className='li-login-description'>
          Log in to your Poetics Stream account to continue building, sharing, 
          and discovering exceptional content.
          </p>
        </div>

        {showLoginForm && (
          <div className='login-form'>
            <div className='login-frame'>
              <h2 className='login-with'>Log in with</h2>
              <div className='button-section'>
                <button
                  type='button'
                  className='media-login-button'
                  onClick={() => (window.location.href = GOOGLE_OAUTH_URL)}
                >
                  <img
                    src='/images/mobile/SSO_buttons/google.svg'
                    alt='Google'
                    className='sso-logo-desktop'
                    style={{ height: 20 }}
                  />
                  Google
                </button>
                  <button
                    type='button'
                    className='media-login-button'
                    onClick={() =>
                      (window.location.href = FACEBOOK_OAUTH_URL)
                    }
                  >
                    <svg
                      viewBox='0 0 320 512'
                      className='sso-logo-desktop'
                      style={{ height: 20, width: 20, flex: 'none' }}
                      aria-hidden='true'
                      focusable='false'
                    >
                      <path
                        fill='#FAFAFA'
                        d='M279.14 288l14.22-92.66h-88.91V117.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z'
                      />
                    </svg>
                    Facebook
                  </button>
              </div>
            </div>
            <div className='li-or-separator'>or</div>
            <div className='inputs-btn'>
              <input
               type='email'
                placeholder='Email'
                className='input-field'
                autoComplete='email'
                required
              />
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder='Password'
                  className='input-field'
                  autoComplete='current-password'
                  required
                />
                <button 
                  type="button" 
                  className="eye-button" 
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="23px" height="30px" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.68788 8.3059C1.36566 7.85735 1.46807 7.23252 1.91662 6.9103C2.36517 6.58809 2.99 6.6905 3.31221 7.13905C3.61607 7.56204 4.08478 7.96552 4.6918 8.31591C5.98518 9.0625 7.7771 9.5 9.7043 9.5C11.6315 9.5 13.4234 9.0625 14.7168 8.31591C15.3238 7.96552 15.7925 7.56204 16.0964 7.13905C16.4186 6.6905 17.0434 6.58809 17.492 6.9103C17.9405 7.23252 18.0429 7.85735 17.7207 8.3059C17.2402 8.97475 16.5568 9.56308 15.7167 10.048C14.1049 10.9784 11.9685 11.5 9.7043 11.5C7.44007 11.5 5.30367 10.9784 3.69195 10.048C2.85179 9.56308 2.16836 8.97475 1.68788 8.3059Z" fill="#a3a3a3"/>
                      <path d="M11 11C11 10.4477 10.5523 10 10 10C9.44772 10 9 10.4477 9 11L9 13.5C9 14.0523 9.44772 14.5 10 14.5C10.5523 14.5 11 14.0523 11 13.5L11 11Z" fill="#a3a3a3"/>
                      <path d="M5.52985 10.7575C5.6638 10.2217 6.20673 9.8959 6.74253 10.0298C7.27832 10.1638 7.60408 10.7067 7.47013 11.2425L6.97013 13.2425C6.83619 13.7783 6.29325 14.1041 5.75746 13.9701C5.22166 13.8362 4.8959 13.2933 5.02985 12.7575L5.52985 10.7575Z" fill="#a3a3a3"/>
                      <path d="M13.9702 10.7575C13.8362 10.2217 13.2933 9.8959 12.7575 10.0298C12.2217 10.1638 11.8959 10.7067 12.0299 11.2425L12.5299 13.2425C12.6638 13.7783 13.2067 14.1041 13.7425 13.9701C14.2783 13.8362 14.6041 13.2933 14.4702 12.7575L13.9702 10.7575Z" fill="#a3a3a3"/>
                      <path d="M16.5249 8.29289C16.1344 7.90237 15.5012 7.90237 15.1107 8.29289C14.7201 8.68342 14.7201 9.31658 15.1107 9.70711L17.1107 11.7071C17.5012 12.0976 18.1344 12.0976 18.5249 11.7071C18.9154 11.3166 18.9154 10.6834 18.5249 10.2929L16.5249 8.29289Z" fill="#a3a3a3"/>
                      <path d="M3.07775 8.32742C3.44921 7.91872 4.08165 7.88853 4.49036 8.25999C4.89906 8.63145 4.92924 9.26389 4.55779 9.67259L2.74002 11.6726C2.36857 12.0813 1.73612 12.1115 1.32742 11.74C0.918719 11.3686 0.88853 10.7361 1.25999 10.3274L3.07775 8.32742Z" fill="#a3a3a3"/>
                    </svg>
                  ) : (
                    <svg width="23px" height="30px" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M10 16.5C14.8972 16.5 19 14.1922 19 11C19 7.80784 14.8972 5.5 10 5.5C5.10282 5.5 1 7.80784 1 11C1 14.1922 5.10282 16.5 10 16.5ZM10 7.5C13.9394 7.5 17 9.2216 17 11C17 12.7784 13.9394 14.5 10 14.5C6.06062 14.5 3 12.7784 3 11C3 9.2216 6.06062 7.5 10 7.5Z" fill="#a3a3a3"/>
                      <path d="M9 3.5C9 2.94772 9.44772 2.5 10 2.5C10.5523 2.5 11 2.94772 11 3.5L11 6.5C11 7.05228 10.5523 7.5 10 7.5C9.44772 7.5 9 7.05228 9 6.5L9 3.5Z" fill="#a3a3a3"/>
                      <path d="M13.0194 3.80383C13.1277 3.26227 13.6546 2.91106 14.1961 3.01937C14.7377 3.12768 15.0889 3.65451 14.9806 4.19606L14.4806 6.69606C14.3723 7.23762 13.8455 7.58884 13.3039 7.48053C12.7623 7.37222 12.4111 6.84539 12.5194 6.30383L13.0194 3.80383Z" fill="#a3a3a3"/>
                      <path d="M6.98063 3.80383C6.98063 3.26227 6.4538 2.91106 5.91224 3.01937C5.37068 3.12768 5.01947 3.65451 5.12778 4.19606L5.62778 6.69606C5.73609 7.23762 6.26292 7.58884 6.80447 7.48053C7.34603 7.37222 7.69725 6.84539 7.58894 6.30383L7.08894 3.80383Z" fill="#a3a3a3"/>
                      <path d="M2.85752 4.98553C2.57338 4.51195 1.95912 4.35839 1.48554 4.64254C1.01196 4.92668 0.858391 5.54094 1.14254 6.01452L2.64254 8.51452C2.92669 8.98811 3.54095 9.14167 4.01453 8.85752C4.48811 8.57337 4.64167 7.95911 4.35752 7.48553L2.85752 4.98553Z" fill="#a3a3a3"/>
                      <path d="M17.1425 4.98553C17.4267 4.51195 18.0409 4.35839 18.5145 4.64254C18.9881 4.92668 19.1417 5.54094 18.8575 6.01452L17.3575 8.51452C17.0734 8.98811 16.4591 9.14167 15.9855 8.85752C15.512 8.57337 15.3584 7.95911 15.6425 7.48553L17.1425 4.98553Z" fill="#a3a3a3"/>
                      <path fillRule="evenodd" clipRule="evenodd" d="M10 14C11.933 14 13.5 12.433 13.5 10.5C13.5 8.567 11.933 7 10 7C8.067 7 6.5 8.567 6.5 10.5C6.5 12.433 8.067 14 10 14ZM10 9C10.8284 9 11.5 9.67157 11.5 10.5C11.5 11.3284 10.8284 12 10 12C9.17157 12 8.5 11.3284 8.5 10.5C8.5 9.67157 9.17157 9 10 9Z" fill="#a3a3a3"/>
                    </svg>
                  )} 
                </button>
              </div>
              <div className='bottom-form'>
                <button className='login-button' onClick={handleLogin}>
                  Log in
                </button>
                <p
                  className='forgot-password-link'
                  onClick={() => setShowResetPassword(true)}
                >
                  Forgot password?
                </p>
              </div>
            </div>
            <div className='signup-prompt'>
              <p className='account'>Want to create an account? </p>
              <span className='signup-link' onClick={() => navigate('/signup')}>
                Sign up
              </span>
            </div>
          </div>
        )}

        {loading && (
          <div className='loading-spinner-container'>
            <div className='spinner' />
          </div>
        )}
      </div>

      {showResetPassword && (
        <div className='modal-overlay'>
          <ResetPassword
            email={email}
            setEmail={setEmail}
            onClose={() => setShowResetPassword(false)}
            onSuccess={onSuccess}
          />
        </div>
      )}

      {showSetNewPassword && (
        <div className='modal-overlay'>
          <NewPassword
            onClose={() => {
              setShowResetPassword(false);
              setShowSetNewPassword(false);
              navigate('/login', { replace: true });
            }}
            onSuccess={onSuccess}
          />
        </div>
      )}
      <p className='li-agreement-text'>
        By continuing you agree to Poetics Stream's{' '}
        <a
          href='/terms-of-service'
          className='agreement-link'
          target='_blank'
          rel='noopener noreferrer'
        >
          Terms of service
        </a>{' '}
        and{' '}
        <a
          href='/privacy-policy'
          className='agreement-link'
          target='_blank'
          rel='noopener noreferrer'
        >
          Privacy policy
        </a>
        .
      </p>
    </div>
  );
};

export default LoginPage;
